import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Outlet, useParams } from "react-router-dom";

import type {
  Assembly,
  AssemblyParticipant,
} from "@tudigo-monorepo/core-tudigo-api-models";
import { ASSEMBLIES } from "@tudigo-monorepo/core-tudigo-utils";
import { useGetAssemblyByIdQuery } from "@tudigo-monorepo/web-tudigo-api-client";

import { ParticipationsAssemblyDetailPageSkeleton } from "./participations-assembly-detail-page/components/participations-assembly-detail-page.skeleton";
import { USER_ASSEMBLY_QUERY_FIELDS } from "./utils/user-assembly-detail-query-fields";

export type ParticipationsAssemblyPageContextType = {
  assembly: Assembly | undefined;
  assemblyParticipant: AssemblyParticipant | null;
  participationFormData: AssemblyParticipant | undefined;
  setParticipationFormData: (myParticipation: AssemblyParticipant) => void;
  isAssemblyOpenForVoting: (assembly: Assembly) => boolean;
  isAssemblyOpenForParticipationUpdate: (assembly: Assembly) => boolean;
  isAssemblyExpired: (assembly: Assembly) => boolean;
};

const ParticipationsAssemblyPageContext =
  createContext<ParticipationsAssemblyPageContextType | null>(null);

export function useParticipationsAssemblyPageContext() {
  const context = useContext(ParticipationsAssemblyPageContext);
  if (context == null) {
    throw new Error(
      "useParticipationsAssemblyPageContext must be used within a ParticipationsAssemblyPageProvider",
    );
  }

  return context;
}

export function ParticipationsAssemblyPageProvider() {
  const { id: assemblyId = "" } = useParams();

  const { data: assembly, isFetching } = useGetAssemblyByIdQuery(assemblyId, {
    fields: USER_ASSEMBLY_QUERY_FIELDS,
  });

  const assemblyParticipant = assembly?.myParticipation ?? null;

  const [participationFormData, setParticipationFormData] = useState(
    assemblyParticipant ? { ...assemblyParticipant } : undefined,
  );

  const isAssemblyExpired = useCallback((assembly: Assembly) => {
    if (assembly.status === "closed") {
      return true;
    }
    const now = new Date();
    const scheduledEndDate = new Date(assembly.scheduledEndDate ?? "");

    return now > scheduledEndDate;
  }, []);

  const isAssemblyOpenForVoting = useCallback((assembly: Assembly) => {
    if (assembly.status === "closed") {
      return false;
    }
    const now = new Date();
    const votingStartDate = new Date(assembly.scheduledStartDate ?? "");
    const votingEndDate = new Date(assembly.scheduledEndDate ?? "");

    return now >= votingStartDate && now <= votingEndDate;
  }, []);

  const isAssemblyOpenForParticipationUpdate = useCallback(
    (assembly: Assembly) => {
      const now = new Date();
      const scheduledEndDate = new Date(assembly.scheduledEndDate ?? "");
      if (assemblyParticipant?.status === "replied") {
        return false;
      }
      if (assembly?.status === ASSEMBLIES.STATUS.STATUS_CLOSED) {
        return false;
      }

      return now <= scheduledEndDate;
    },
    [assemblyParticipant],
  );

  const contextValue = useMemo<ParticipationsAssemblyPageContextType>(
    () => ({
      assembly,
      assemblyParticipant,
      participationFormData,
      setParticipationFormData,
      isAssemblyOpenForVoting,
      isAssemblyOpenForParticipationUpdate,
      isAssemblyExpired,
    }),
    [assembly, assemblyParticipant, participationFormData],
  );

  useEffect(() => {
    if (assemblyParticipant !== null) {
      setParticipationFormData({ ...assemblyParticipant });
    }
  }, [assemblyParticipant]);

  if (isFetching) {
    return <ParticipationsAssemblyDetailPageSkeleton />;
  } else if (!assembly || !assemblyParticipant) {
    return null;
  } else {
    return (
      <ParticipationsAssemblyPageContext.Provider value={contextValue}>
        <Outlet />
      </ParticipationsAssemblyPageContext.Provider>
    );
  }
}
